/**
 * @module ui/templates/btn
 */
import tgamConfig from "config/tgam";

/**
 * @method button
 * @description Generate the markup for a generic button element.
 * @param {Object} c - collection of properties
 * @param {String} c.id - attribute value of button
 * @param {String} c.type - of element (`a` or `button`)
 * @param {String} [c.href] - for anchor buttons only
 * @param {String} [c.icon] - from sprite.svg
 * @param {String} [c.text] - to display within button
 * @param {Array} [c.classes=[]] - classes to append to button
 * @param {Object} [c.attrs={}] - of kvp attributes
 * @returns {String}
 */
export function button({ id, type, href, icon, text, classes = [], attrs = {} }) {
  const btnID = (id && id !== "") ? ` id="${id}"` : "";
  const element = (type && type === "a") ? `a href="${href || "#"}"` : "button";
  const klasses = (classes && Array.isArray(classes)) ? (" " + classes.join(" ")) : "";

  const symbol = icon ? `<svg class="c-button__icon" role="img" pointer-events="none" focusable="false" aria-hidden="true">
    <use xlink:href="${tgamConfig.env.svgSpritePath}#${icon}"></use>
  </svg>` : "";

  const copy = text ? `<span class="c-button__text">${text}</span>` : "";

  let attributes = "";
  Object.keys(attrs).forEach(key => {
    attributes += ` ${key}="${attrs[key]}"`;
  });

  return `<${element}${btnID} class="c-button${klasses}"${attributes}>${symbol}${copy}</${element}>`;
}

/**
 * @method closeButton
 * @description Generate the markup for a close button element.
 * @param {Object} c - collection of properties
 * @param {String} [c.id=""] - attribute value of button
 * @param {Array} [c.classes=[]] - classes to append to button
 * @param {String} [c.label="Close"] - for button
 * @returns {String}
 */
export function closeButton({ id = "", classes = [], label = "Close" }) {
  return button({
    id: id,
    type: "button",
    icon: "close-x",
    classes: ["c-button--icon-only", "c-button--no-border"].concat(classes),
    attrs: {
      type: "button",
      "aria-label": label
    }
  });
}

/**
 * @description The markup for a financial modal close button.
 * Specifically to display to a registered or subscribed user.
 */
export const CLOSE_BUTTON_PREMIUM = closeButton({
  id: "js-modal-logged-in-close",
  classes: ["c-button--light-blue-secondary", "c-button--financial-close"],
  label: "Close welcome message modal"
});

/**
 * @description The markup for a financial modal close button.
 * Specifically to display to a registered or anonymous user.
 */
export const CLOSE_BUTTON_ANON = closeButton({
  id: "js-modal-anonymous-close",
  classes: ["c-button--light-blue-secondary", "c-button--financial-close"],
  label: "Close welcome message modal"
});

/**
 * @description Returns the markup for a engagement dismiss close button.
 * @param {String} featureName - Name of feaure to use for aria label.
 * @returns {String}
 */
export function getCloseButtonEngageHTML(featureName) {
  return closeButton({
    id: "js-engage-article-drop-down-close",
    classes: ["c-button--icon-only", "c-button--small", "c-button--no-border"],
    label: `Close ${featureName}`
  });
}
